import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Menu, X, Phone, Mail, MapPin, Instagram, Facebook, Twitter } from 'lucide-react';
import Home from './components/Home';
import Services from './components/Services';
import Gallery from './components/Gallery';
import Contact from './components/Contact';
import Team from './components/Team';
import AboutUs from './components/AboutUs';
import Survey from './components/Survey';
import AdminLayout from './components/admin/AdminLayout';
import MobileAdmin from './components/MobileAdmin';
import ApiService from './services/api';

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);

  // Menü kapanması için
  const closeMenu = () => setIsMenuOpen(false);

  // HomeSettings yükle
  useEffect(() => {
    const loadHomeSettings = async () => {
      try {
        const response = await ApiService.getHomeSettings();
        if (response && response.settings) {
          setHomeSettings(response.settings);
        }
      } catch (error) {
        console.error('Home settings load error:', error);
      }
    };

    loadHomeSettings();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <div className="min-h-screen">
        {/* Navigation */}
        <nav style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: '#f2eadd',
          border: 'none',
          backdropFilter: 'none',
          display: (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/x0vg5fgdfvDfv45')) ? 'none' : 'block'
        }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between md:justify-center items-center py-4 md:py-6">
              
              {/* Logo/Brand - Mobile Only */}
              <div className="md:hidden">
                <Link to="/" className="text-black font-medium text-lg" style={{ fontFamily: 'FuturaLTPaneuropean' }}>
                  SHE'VA
                </Link>
              </div>
              
              {/* Desktop Menu - Centered */}
              <div className="hidden md:flex space-x-8 lg:space-x-12">
                <Link to="/" className="text-black hover:text-gray-700 transition-colors font-light text-sm lg:text-base tracking-wide" style={{ fontFamily: 'FuturaLTPaneuropean' }}>Ana Sayfa</Link>
                <Link to="/contact" className="text-black hover:text-gray-700 transition-colors font-light text-sm lg:text-base tracking-wide" style={{ fontFamily: 'FuturaLTPaneuropean' }}>Randevu</Link>
                <Link to="/about" className="text-black hover:text-gray-700 transition-colors font-light text-sm lg:text-base tracking-wide" style={{ fontFamily: 'FuturaLTPaneuropean' }}>Hakkımızda</Link>
                <Link to="/services" className="text-black hover:text-gray-700 transition-colors font-light text-sm lg:text-base tracking-wide" style={{ fontFamily: 'FuturaLTPaneuropean' }}>Hizmetler</Link>
                <Link to="/gallery" className="text-black hover:text-gray-700 transition-colors font-light text-sm lg:text-base tracking-wide" style={{ fontFamily: 'FuturaLTPaneuropean' }}>Galeri</Link>
              </div>

              {/* Mobile Menu Button - Mobile Only */}
              <button 
                className="md:hidden p-2 rounded-lg transition-colors hover:bg-gray-200"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="w-6 h-6 text-black" /> : <Menu className="w-6 h-6 text-black" />}
              </button>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
              <div className="md:hidden border-t border-gray-300 bg-white shadow-lg">
                <div className="flex flex-col py-2">
                  <Link 
                    to="/" 
                    className="text-black hover:bg-gray-100 transition-colors font-light py-3 px-4 tracking-wide border-b border-gray-100"
                    style={{ fontFamily: 'FuturaLTPaneuropean' }}
                    onClick={closeMenu}
                  >
                    Ana Sayfa
                  </Link>
                  <Link 
                    to="/contact" 
                    className="text-black hover:bg-gray-100 transition-colors font-light py-3 px-4 tracking-wide border-b border-gray-100"
                    style={{ fontFamily: 'FuturaLTPaneuropean' }}
                    onClick={closeMenu}
                  >
                    Randevu
                  </Link>
                  <Link 
                    to="/about" 
                    className="text-black hover:bg-gray-100 transition-colors font-light py-3 px-4 tracking-wide border-b border-gray-100"
                    style={{ fontFamily: 'FuturaLTPaneuropean' }}
                    onClick={closeMenu}
                  >
                    Hakkımızda
                  </Link>
                  <Link 
                    to="/services" 
                    className="text-black hover:bg-gray-100 transition-colors font-light py-3 px-4 tracking-wide border-b border-gray-100"
                    style={{ fontFamily: 'FuturaLTPaneuropean' }}
                    onClick={closeMenu}
                  >
                    Hizmetler
                  </Link>
                  <Link 
                    to="/gallery" 
                    className="text-black hover:bg-gray-100 transition-colors font-light py-3 px-4 tracking-wide"
                    style={{ fontFamily: 'FuturaLTPaneuropean' }}
                    onClick={closeMenu}
                  >
                    Galeri
                  </Link>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main className="pt-12 md:pt-16">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/team" element={<Team />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/survey/:token" element={<Survey />} />
            <Route path="/admin" element={<AdminLayout />} />
            <Route path="/x0vg5fgdfvDfv45" element={<MobileAdmin />} />
          </Routes>
        </main>
      </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
